import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { BackIcon, CalendarIcon } from '@/components/pulse/icons';

/** Pulse's business dates are New York dates (D11). */
const BUSINESS_TIME_ZONE = 'America/New_York';
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** Today's business date, as YYYY-MM-DD. */
export function todayInNewYork(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: BUSINESS_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

/** A business date moved by whole days. */
export function stepDate(date: string, days: number): string {
  const moved = new Date(Date.parse(`${date}T12:00:00Z`) + days * 24 * 60 * 60 * 1000);
  return moved.toISOString().slice(0, 10);
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * "Fri 25 Sep 2026", as the prototype's date pill reads. Written out rather than left to the
 * locale, whose short month for September is "Sept" in some browsers.
 */
export function formatDayPill(date: string): string {
  const day = new Date(`${date}T12:00:00Z`);
  return `${WEEKDAYS[day.getUTCDay()]} ${day.getUTCDate()} ${MONTHS[day.getUTCMonth()]} ${day.getUTCFullYear()}`;
}

/** The page's business date from its URL (`?date=`), never later than today (SHL-4). */
export function dateFromSearch(search: URLSearchParams, today: string): string {
  const value = search.get('date');
  if (value === null || !DATE_PATTERN.test(value) || Number.isNaN(Date.parse(`${value}T12:00:00Z`))) return today;
  return value > today ? today : value;
}

/** The query string for a date: none for today, so today's URL stays the plain one. */
export function dateSearch(date: string, today: string): string {
  return date >= today ? '' : `?date=${date}`;
}

/** The business date a day page shows, and today's, from the URL. */
export function useBusinessDate(): { readonly date: string; readonly today: string } {
  const [search] = useSearchParams();
  const today = todayInNewYork();
  return { date: dateFromSearch(search, today), today };
}

/**
 * A day page's control (SHL-4, the prototype's `control:'day'`): the date between arrows that step
 * one business date. The next arrow is disabled on today, and the date lives in the URL.
 */
export function DayControl({ date, today }: { readonly date: string; readonly today: string }): React.ReactElement {
  const navigate = useNavigate();
  const go = (days: number) => navigate({ search: dateSearch(stepDate(date, days), today) });
  const onToday = date >= today;
  return (
    <div className="row" style={{ gap: 6 }} role="group" aria-label="Day">
      <button type="button" className="btn" aria-label="Previous day" onClick={() => go(-1)}>
        <BackIcon />
      </button>
      <span className="datepill">
        <CalendarIcon />
        <span>{formatDayPill(date)}</span>
      </span>
      <button type="button" className="btn" aria-label="Next day" disabled={onToday} title={onToday ? 'This is today' : undefined} onClick={() => go(1)}>
        <span style={{ display: 'inline-flex', transform: 'rotate(180deg)' }}>
          <BackIcon />
        </span>
      </button>
    </div>
  );
}

/** The Monday of a business date's week. */
export function mondayOf(date: string): string {
  const weekday = new Date(`${date}T12:00:00Z`).getUTCDay();
  return stepDate(date, -((weekday + 6) % 7));
}

/** "Week of 5 Oct 2026" */
export function formatWeekPill(monday: string): string {
  const day = new Date(`${monday}T12:00:00Z`);
  return `Week of ${day.getUTCDate()} ${MONTHS[day.getUTCMonth()]} ${day.getUTCFullYear()}`;
}

/**
 * A week page's control (SHP-P4): the week between arrows that step a week. The next arrow is
 * disabled on the current week, and the week lives in the URL as its Monday; the current week's
 * URL stays the plain one.
 */
export function WeekControl({ week, today }: { readonly week: string; readonly today: string }): React.ReactElement {
  const navigate = useNavigate();
  const current = mondayOf(today);
  const go = (days: number) => {
    const target = stepDate(week, days);
    navigate({ search: target >= current ? '' : `?week=${target}` });
  };
  const onCurrent = week >= current;
  return (
    <div className="row" style={{ gap: 6 }} role="group" aria-label="Week">
      <button type="button" className="btn" aria-label="Previous week" onClick={() => go(-7)}>
        <BackIcon />
      </button>
      <span className="datepill">
        <CalendarIcon />
        <span>{formatWeekPill(week)}</span>
      </span>
      <button type="button" className="btn" aria-label="Next week" disabled={onCurrent} title={onCurrent ? 'This is the current week' : undefined} onClick={() => go(7)}>
        <span style={{ display: 'inline-flex', transform: 'rotate(180deg)' }}>
          <BackIcon />
        </span>
      </button>
    </div>
  );
}

/** The week a week page shows (`?week=`, any date in it), never later than the current week. */
export function useBusinessWeek(): { readonly week: string; readonly today: string } {
  const [search] = useSearchParams();
  const today = todayInNewYork();
  const value = search.get('week');
  const date = value === null || !DATE_PATTERN.test(value) || Number.isNaN(Date.parse(`${value}T12:00:00Z`)) ? today : value > today ? today : value;
  return { week: mondayOf(date), today };
}

/** An as-of page's pill (SHL-4, the prototype's `control:'now'`): no arrows, just when. */
export function AsOfPill({ label }: { readonly label: string | null }): React.ReactElement {
  return (
    <span className="datepill" title="Shows the state right now">
      <CalendarIcon />
      <span>{label ?? 'Not collected yet'}</span>
    </span>
  );
}

/** A state panel on a past day with no stored end-of-day state (SHL-5). */
export function NotRecorded(): React.ReactElement {
  return <span style={{ color: 'var(--muted)' }}>Not recorded for this day</span>;
}
